// Tech purchase orders (Part requests): a tech opens a numbered PO from the techs app, picks a vendor,
// lists part #s and quantities (no prices), and sends it to the office. The office prices it,
// approves it (it becomes a normal /inventoryPOs record with the same number, received on
// Inventory › Receiving) or declines it. Pure (no Firebase, no DOM): tests/unit/partRequests.test.mjs.
//
//   /poRequests/{id}  { Ref: 'PO-2026-041', Status, By_Uid, By_Name, Created_At, Sent_At?,
//                       Vendor_Id, Vendor_Name, Work_Order, Op_Id, Op_Title, Boat_Name, Customer_Name, Customer_ID,
//                       Lines: [{ partNumber, description, qty, partId?, note? }], Note, Called_Vendor, Spoke_To,
//                       Decided_By, Decided_At, Decline_Reason, Po_Id, Expected_Date ('YYYY-MM-DD', the earliest
//                       still due on the PO; kept in step by functions/poRequests.js),
//                       Arrived: { state: partial|received, at, text }, Tech_Cleared }
// Status: draft (the tech's, not sent) → waiting (on the office) → ordered | declined; or cancelled.
// The id of an approved request is also its /inventoryPOs id.

import { receiveBin, UNASSIGNED, lineDue, poLines as linesOf } from './poReceivingCore.js';

export const STATUSES = ['draft', 'waiting', 'ordered', 'declined', 'cancelled'];
export const MAX_LINES = 30;
const str = v => (v == null ? '' : String(v)).trim();
const whole = n => Math.max(0, Math.round(Number(n) || 0));

/** A tech's line, cleaned: part # and description trimmed, qty a whole number (1 if blank). Never a price. */
export function cleanLine(l = {}) {
  const line = {
    partNumber: str(l.partNumber).slice(0, 60),
    description: str(l.description).slice(0, 200),
    qty: Math.min(9999, whole(l.qty === '' || l.qty == null ? 1 : l.qty)),
  };
  if (str(l.partId)) line.partId = str(l.partId);
  if (str(l.note)) line.note = str(l.note).slice(0, 200);
  return line;
}

export const isBlankLine = l => !str(l?.partNumber) && !str(l?.description);

/** Lines worth keeping (blank rows dropped). */
export const keptLines = lines => (Array.isArray(lines) ? lines : Object.values(lines || {})).filter(l => l && !isBlankLine(l)).map(cleanLine);

/** What stops a tech sending it: [] when it can go. */
export function sendProblems(req, lang = 'EN') {
  const es = lang === 'ES';
  const out = [];
  if (!str(req?.Vendor_Name)) out.push(es ? 'Elige un proveedor' : 'Pick a vendor');
  const lines = keptLines(req?.Lines);
  if (!lines.length) out.push(es ? 'Añade al menos una pieza' : 'Add at least one part');
  if (lines.some(l => !l.qty)) out.push(es ? 'Cada pieza necesita una cantidad' : 'Every part needs a quantity');
  if (lines.length > MAX_LINES) out.push(es ? `Máximo ${MAX_LINES} piezas` : `At most ${MAX_LINES} parts`);
  return out;
}

/** The new request a tech starts from an operation. */
export function newRequest({ ref, uid, name, op = {}, at = Date.now() }) {
  if (!uid) throw new Error('Not signed in');
  if (!str(ref)) throw new Error('No PO number');
  const r = {
    Ref: str(ref), Status: 'draft', By_Uid: uid, By_Name: str(name).slice(0, 80) || 'Tech', Created_At: at,
    Vendor_Id: '', Vendor_Name: '', Lines: [{ partNumber: '', description: '', qty: 1 }], Note: '', Called_Vendor: false, Spoke_To: '',
  };
  const opId = str(op.id || op.Job_ID);
  if (str(op.Work_Order)) r.Work_Order = str(op.Work_Order);
  if (opId) r.Op_Id = opId;
  if (str(op.Op_Title)) r.Op_Title = str(op.Op_Title).slice(0, 120);
  if (str(op.Boat_Name)) r.Boat_Name = str(op.Boat_Name);
  if (str(op.Customer_Name)) r.Customer_Name = str(op.Customer_Name);
  if (str(op.Customer_ID)) r.Customer_ID = str(op.Customer_ID);
  return r;
}

/** The tech may still change it (and withdraw it) until the office acts. */
export const techCanEdit = req => !!req && (req.Status === 'draft' || req.Status === 'waiting');

/** On the tech's Today card: everything not finished with, until they tap Got it on a done one. */
export function techActive(req) {
  if (!req || req.Tech_Cleared || req.Status === 'cancelled') return false;
  return true;
}

/** Where it stands, for the tech (and the office list): { key, text, tone }. */
export function statusOf(req, lang = 'EN') {
  const es = lang === 'ES';
  const s = req?.Status;
  const arrived = req?.Arrived?.state;
  if (s === 'ordered' && arrived === 'received') return { key: 'arrived', text: es ? 'Llegó' : 'Arrived', tone: 'green' };
  if (s === 'ordered' && arrived === 'partial') return { key: 'partial', text: es ? 'Llegó una parte' : 'Partly in', tone: 'amber' };
  return ({
    draft: { key: 'draft', text: es ? 'Borrador · sin enviar' : 'Draft · not sent', tone: 'grey' },
    waiting: { key: 'waiting', text: es ? 'Esperando a la oficina' : 'Waiting for office', tone: 'amber' },
    ordered: { key: 'ordered', text: es ? 'Pedido' : 'Ordered', tone: 'blue' },
    declined: { key: 'declined', text: es ? 'Rechazado' : 'Declined', tone: 'red' },
    cancelled: { key: 'cancelled', text: es ? 'Cancelado' : 'Cancelled', tone: 'grey' },
  })[s] || { key: 'draft', text: s || '', tone: 'grey' };
}

/** The tech's list: open ones first (waiting, ordered, drafts), then done ones; newest first in each. */
export function techList(reqs) {
  const rank = r => ({ declined: 0, waiting: 2, draft: 3 })[r.Status] ?? (r.Arrived?.state === 'received' ? 0 : 1);
  return Object.entries(reqs || {})
    .filter(([, r]) => r && techActive(r))
    .map(([id, r]) => ({ id, ...r }))
    .sort((a, b) => rank(a) - rank(b) || (b.Sent_At || b.Created_At || 0) - (a.Sent_At || a.Created_At || 0));
}

/** The office inbox: filter waiting | ordered | all (never the techs' drafts). Waiting oldest first, else newest. */
export function officeList(reqs, filter = 'waiting') {
  const list = Object.entries(reqs || {})
    .filter(([, r]) => r && r.Status && r.Status !== 'draft')
    .map(([id, r]) => ({ id, ...r }))
    .filter(r => filter === 'all' || r.Status === filter);
  const at = r => r.Sent_At || r.Created_At || 0;
  return list.sort((a, b) => (filter === 'waiting' ? at(a) - at(b) : at(b) - at(a)));
}

export const waitingCount = reqs => Object.values(reqs || {}).filter(r => r && r.Status === 'waiting').length;

/**
 * The /inventoryPOs record the office's approval makes (the same id and number). prices: { lineIndex: cents }
 * (a line left blank stays unpriced). lines: the office's edited lines (else the request's). parts: inventory
 * (for the bin a line goes to).
 */
export function poFromRequest(req, { id, lines, prices = {}, parts = [], user = {}, placedLabel, now = Date.now(), eta = '' }) {
  const byId = new Map((parts || []).map(p => [p.id, p]));
  const poLines = keptLines(lines || req.Lines).map((l, i) => {
    const p = l.partId ? byId.get(l.partId) : null;
    // A stocked part goes to its bin (or Unassigned: Receiving asks for one); anything else to Receiving.
    const line = { ordered: l.qty, received: 0, bin: p ? receiveBin(p.Bins, '') : 'Receiving', partNumber: l.partNumber || p?.Part_Number || '', description: l.description || p?.Description || '' };
    if (l.partId && p) line.partId = l.partId;
    const c = prices[i];
    if (c != null && c !== '' && Number.isFinite(Number(c))) line.priceCents = Math.max(0, Math.round(Number(c)));
    const note = [l.note, req.Work_Order ? `WO ${req.Work_Order}` : ''].filter(Boolean).join(' · ');
    if (note) line.note = note.slice(0, 200);
    if (req.Op_Id) line.Job_ID = req.Op_Id; // the Receiving tab's "for WO" chip
    if (req.Work_Order) line.Work_Order = req.Work_Order;
    if (isDay(eta)) line.Expected_Date = eta; // the work order's "Parts on order · ETA …"
    return line;
  });
  const priced = poLines.filter(l => l.priceCents != null);
  const po = {
    id, ref: req.Ref, open: true,
    vendor: req.Vendor_Name, vendorId: req.Vendor_Id || null,
    placed: placedLabel, placedAt: now, placedBy: user.name || '', placedByUid: user.uid || null,
    fromRequest: { id, by: req.By_Name || '', byUid: req.By_Uid || '', wo: req.Work_Order || '', opId: req.Op_Id || '', boat: req.Boat_Name || '', calledVendor: !!req.Called_Vendor },
    lines: poLines,
    totalCents: priced.reduce((t, l) => t + l.ordered * l.priceCents, 0),
    unpriced: poLines.length - priced.length,
  };
  return po;
}

// ─── Expected delivery ───

const DAY = /^\d{4}-\d{2}-\d{2}$/;
export const isDay = v => DAY.test(str(v));
const dayMs = d => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));

/** 'YYYY-MM-DD' plus n days. */
export function addDays(day, n) {
  const d = new Date(dayMs(day) + Math.round(Number(n) || 0) * 864e5);
  return d.toISOString().slice(0, 10);
}

/** The vendor's lead time from today ('' without one). */
export const etaFromLead = (leadDays, today) => (whole(leadDays) > 0 && isDay(today) ? addDays(today, whole(leadDays)) : '');

/** "Nov 7" ('' when not a day). */
export function dayLabel(day, lang = 'EN') {
  if (!isDay(day)) return '';
  return new Date(dayMs(day)).toLocaleDateString(lang === 'ES' ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "Expected Nov 7" · "Due today" · "Expected Nov 7, 2 days late" ('' without a date). */
export function etaText(day, today, lang = 'EN') {
  if (!isDay(day)) return '';
  const es = lang === 'ES';
  const late = isDay(today) ? Math.round((dayMs(today) - dayMs(day)) / 864e5) : 0;
  if (late === 0) return es ? 'Llega hoy' : 'Due today';
  if (late > 0) return es ? `Esperada ${dayLabel(day, lang)}, ${late} día${late === 1 ? '' : 's'} tarde` : `Expected ${dayLabel(day, lang)}, ${late} day${late === 1 ? '' : 's'} late`;
  return es ? `Esperada ${dayLabel(day, lang)}` : `Expected ${dayLabel(day, lang)}`;
}

/** The PO's expected delivery: the earliest date on a line still due ('' when none has one). */
export function poEta(po) {
  if (!po || po.open === false) return '';
  const lines = linesOf(po);
  const due = lines.map((l, i) => (lineDue(po, i) > 0 ? l?.Expected_Date : '')).filter(isDay).sort();
  return due[0] || '';
}

/** The /inventoryPOs line fields that set (or clear) the expected date on every line still due. */
export function etaFields(po, day) {
  const out = {};
  linesOf(po).forEach((l, i) => { if (l && lineDue(po, i) > 0) out[`lines/${i}/Expected_Date`] = isDay(day) ? day : null; });
  return out;
}

/**
 * A new /inventory part for a line that isn't stocked yet, so Receiving puts it on a shelf and the
 * tech can take it onto the work order like any part. On hand 0 (it's on order), no bin yet
 * (Receiving asks for one), cost = the price typed, the PO's vendor, no reorder levels.
 */
export function newPartRecord(line, { id, vendorName = '', priceCents = null, today, who = '', ref = '', wo = '' }) {
  const pn = str(line?.partNumber) || `NEW-${String(id).slice(0, 6)}`;
  return {
    id, Part_Number: pn.slice(0, 60), Description: (str(line?.description) || pn).slice(0, 200), Manufacturer: '',
    Vendor_List: str(vendorName) ? [str(vendorName)] : [], Bins: [{ name: UNASSIGNED, qty: 0 }], Qty: 0, Location: UNASSIGNED,
    Min: 0, Max: 0, Cost: priceCents != null && Number.isFinite(Number(priceCents)) ? Math.round(Number(priceCents)) / 100 : 0, Sale_Price: 0,
    Add_Date: today, Usage_History: [],
    Adjustment_Log: [{ date: today, who, change: 0, reason: `Added from ${ref || 'a tech PO'}${wo ? ` (${wo})` : ''}` }],
  };
}

/** "$12.50" typed by the office → cents (null when blank or not a number). */
export function parseCents(v) {
  const s = str(v).replace(/[$,\s]/g, '');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

/**
 * What arrived, for the tech: "2 × 18-3254 → bin B-3; 1 × impeller kit → Receiving". partBins: { partId: Bins }
 * (the part's bins now), so a line marked Unassigned names the bin Receiving really put it in.
 */
export function arrivedText(po, receiptLines = {}, lang = 'EN', partBins = {}) {
  const lines = Array.isArray(po?.lines) ? po.lines : Object.values(po?.lines || {});
  const word = lang === 'ES' ? 'estante' : 'bin';
  return Object.entries(receiptLines || {})
    .map(([k, n]) => [lines[Number(String(k).replace(/^l/, ''))], whole(n)])
    .filter(([l, n]) => l && n)
    .map(([l, n]) => {
      const bin = l.partId ? receiveBin(partBins[l.partId], l.bin) : l.bin;
      return `${n} × ${l.partNumber || l.description || '?'} → ${bin && bin !== 'Receiving' && bin !== UNASSIGNED ? `${word} ${bin}` : 'Receiving'}`;
    })
    .join('; ');
}

/** What the thread shows when things happen (an event pill in Ask the office). */
export function eventText(kind, req, extra = '') {
  const lines = keptLines(req?.Lines).length;
  const what = `${req?.Ref || 'PO'} · ${req?.Vendor_Name || ''} · ${lines} part${lines === 1 ? '' : 's'}`;
  return ({
    sent: `Sent ${what} to the office${req?.Called_Vendor ? ' (already ordered by phone)' : ''}`,
    ordered: `Approved ${what}${isDay(req?.Expected_Date) ? ` · expected ${dayLabel(req.Expected_Date)}` : ''}`,
    eta: isDay(extra) ? `${req?.Ref || 'PO'} now expected ${dayLabel(extra)}` : `${req?.Ref || 'PO'}: no delivery date now`,
    declined: `Declined ${req?.Ref || 'PO'}${extra ? `: ${extra}` : ''}`,
    arrived: `Parts in for ${req?.Ref || 'PO'}${extra ? `: ${extra}` : ''}`,
  })[kind] || what;
}

/** tel: link for a phone typed any way ("(631) 555-0101 x12" → "tel:6315550101,12"). */
export function telHref(phone) {
  const s = str(phone);
  if (!s) return '';
  const [main, ext] = s.split(/\s*(?:x|ext\.?|extension)\s*/i);
  const digits = main.replace(/[^\d+]/g, '');
  if (digits.replace(/\D/g, '').length < 7) return '';
  const e = (ext || '').replace(/\D/g, '');
  return `tel:${digits}${e ? `,${e}` : ''}`;
}

/** The vendor's contact card rows for a tech: [{ key, label, value, href? }] (empty ones left out). */
export function vendorContact(v, lang = 'EN') {
  if (!v) return [];
  const es = lang === 'ES';
  const rows = [
    { key: 'phone', label: es ? 'Teléfono' : 'Phone', value: str(v.Phone), href: telHref(v.Phone) },
    { key: 'rep', label: es ? 'Representante' : 'Sales rep', value: str(v.Rep_Name) },
    { key: 'repPhone', label: es ? 'Tel. del representante' : 'Rep phone', value: str(v.Rep_Phone), href: telHref(v.Rep_Phone) },
    { key: 'account', label: es ? 'Nuestra cuenta #' : 'Our account #', value: str(v.Account_Number) },
    { key: 'email', label: 'Email', value: str(v.Rep_Email || v.Email || v.Order_Email), href: str(v.Rep_Email || v.Email || v.Order_Email) ? `mailto:${str(v.Rep_Email || v.Email || v.Order_Email)}` : '' },
    { key: 'web', label: es ? 'Sitio web' : 'Website', value: str(v.Website), href: str(v.Website) ? (/^https?:\/\//i.test(str(v.Website)) ? str(v.Website) : `https://${str(v.Website)}`) : '' },
    { key: 'address', label: es ? 'Dirección' : 'Address', value: str(v.Address), href: str(v.Address) ? `https://maps.google.com/?q=${encodeURIComponent(str(v.Address))}` : '' },
    { key: 'lead', label: es ? 'Plazo' : 'Lead time', value: v.Lead_Days ? `${whole(v.Lead_Days)} ${es ? 'días' : 'days'}` : '' },
  ];
  return rows.filter(r => r.value);
}

/** Active vendors for the picker, by name: [{ id, ...v }]. */
export function vendorChoices(list) {
  return Object.entries(list || {})
    .filter(([, v]) => v && str(v.Vendor_Name) && !v.Inactive)
    .map(([id, v]) => ({ id, ...v }))
    .sort((a, b) => str(a.Vendor_Name).localeCompare(str(b.Vendor_Name)));
}
